import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import Table from '../../../../component/table/Table';
import { EVENT_LABEL } from '../../../../component/mapTripReport/mapColors';
import OdKpiRow from '../components/OdKpiRow';
import odTableStyles from '../components/odTableStyles';
import OdCoordCell from '../components/OdCoordCell';
import OdSelect from '../components/OdSelect';
import th from '../components/odColumnHeader';
import { buildTripNumbers, compactDuration } from '../odAnalyticsUtils';

const num = (value) => <span className="od-cell-num">{value}</span>;

/** Nulls are load-bearing here: g is absent on ~half of events, kmh whenever speed is. */
const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

/** 'typeChange' → 'Type Change'; an already-spaced label is left alone. */
const humanise = (value) => String(value ?? '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim() || '—';

/**
 * Event type → fleetconsole's own wording, from the map's label map.
 *
 * `humanise` cannot do this job: the server emits SCREAMING_SNAKE (`HARSH_BRAKE`), and splitting
 * that on capitals gives "H A R S H_ B R A K E". Reading EVENT_LABEL rather than restating the
 * four strings here is what keeps the column, the map legend and the tape's tick tooltips saying
 * the same words for the same event.
 */
const eventLabel = (type) => EVENT_LABEL[type] || humanise(type);

const kmh = (value) => (Number.isFinite(value) ? num(`${Math.round(value)}`) : num('—'));

/**
 * Badge hue per event type, matched on a substring for the same reason the tiles are:
 * a label change ('harshBraking' vs 'Harsh braking') must not silently drop the colour.
 */
const EVENT_TONES = {
    brak: 'brake', accel: 'accel', corner: 'corner', overspeed: 'speed',
};
const eventBadge = (type) => {
    const key = String(type ?? '').toLowerCase();
    const tone = Object.keys(EVENT_TONES).find((needle) => key.includes(needle));
    return <span className={`od-badge od-badge-${EVENT_TONES[tone] || 'neutral'}`}>{eventLabel(type)}</span>;
};

/**
 * Harsh-driving and overspeed events for the requested window.
 *
 * Rows come from GET /v3/trip/analytics (`data.events`), which edge-detects greenDriving
 * transitions and pairs the overspeeding open/close markers server-side. Both are
 * window-scoped, not per-trip, so a multi-asset report mixes assets and the Asset column
 * appears only when it needs to.
 */
export const safetyColumns = ({ showAsset, tripNumbers, zone }) => [
    showAsset && {
        id: 'asset',
        name: th('Asset'),
        selector: (row) => row.assetName,
        sortable: true,
        minWidth: '8rem',
        cell: (row) => (
            <div className="od-cell-stack">
                <span className="od-cell-strong">{row.assetName}</span>
                <span className="od-cell-muted">{row.deviceSerial || '—'}</span>
            </div>
        ),
    },
    {
        id: 'start',
        name: th('Start', 'When the event was recorded, in the agency timezone.'),
        selector: (row) => row.startTime,
        sortable: true,
        // Wide enough for the whole stamp on one line: at 9rem the DD-MM-YYYY date wrapped under
        // its own clock, because a column here is sized by its minWidth rather than its content.
        minWidth: '11rem',
        cell: (row) => (row.startTime ? moment.tz(row.startTime, zone).format('DD-MM-YYYY HH:mm:ss') : '—'),
    },
    {
        id: 'event',
        name: th('Event', 'What kind of event: harsh acceleration, braking, cornering, or overspeeding.'),
        selector: (row) => row.type,
        sortable: true,
        minWidth: '8rem',
        cell: (row) => eventBadge(row.type),
    },
    {
        id: 'trip',
        name: th('Trip', 'Which trip the event fell inside. A dash means it happened between trips.'),
        selector: (row) => row.tripNo,
        sortable: true,
        right: true,
        minWidth: '4.5rem',
        // The server's window index is the fallback, not the source: see buildTripNumbers.
        cell: (row) => num(dash(tripNumbers.get(row.tripId) ?? row.tripNo)),
    },
    {
        id: 'startKmh',
        name: th('Start km/h', 'Speed the device reported when the episode opened. Overspeeding only.'),
        selector: (row) => row.startKmh,
        sortable: true,
        right: true,
        minWidth: '5.75rem',
        cell: (row) => kmh(row.startKmh),
    },
    {
        id: 'peakKmh',
        name: th('Peak km/h', 'Overspeeding: the highest speed reached during the episode. Harsh events: the speed at the moment it fired.'),
        selector: (row) => row.peakKmh,
        sortable: true,
        right: true,
        minWidth: '5.75rem',
        cell: (row) => kmh(row.peakKmh),
    },
    {
        id: 'duration',
        name: th('Duration', 'How long the event lasted.'),
        selector: (row) => row.durationSec,
        sortable: true,
        right: true,
        minWidth: '5.5rem',
        cell: (row) => num(compactDuration(row.durationSec)),
    },
    {
        id: 'g',
        name: th('Force (g)', 'Force of the harsh event. 1 g is the pull of gravity — about 0.3 g is a firm brake, 0.5 g and above is severe. Blank for overspeeding, which has no g-force.'),
        selector: (row) => row.g,
        sortable: true,
        right: true,
        minWidth: '5rem',
        cell: (row) => num(Number.isFinite(row.g) ? row.g.toFixed(2) : '—'),
    },
    {
        id: 'closedBy',
        name: th('Closed by', 'How the overspeed episode ended: paired with a closing marker, or left unpaired.'),
        selector: (row) => row.closedBy,
        sortable: true,
        minWidth: '6rem',
        cell: (row) => humanise(row.closedBy),
    },
    {
        id: 'location',
        name: th('Peak location', 'Where the highest-speed sample of the episode was recorded. Click the row to see the point on the Route Map, the coordinates to open Google Maps, or ⧉ to copy them.'),
        // Sorted by latitude, which groups a route north-to-south — the only ordering coordinates
        // have. The address is still what the reader sees on hover.
        selector: (row) => row.lat,
        sortable: true,
        grow: 2,
        minWidth: '13rem',
        cell: (row) => <OdCoordCell lat={row.lat} lon={row.lon} address={row.location} />,
    },
].filter(Boolean);

/** fleetconsole's `evTypeSel`, in the Event column's own words. `''` is no filter. */
const TYPE_OPTIONS = [
    { value: '', label: 'All events' },
    { value: 'HARSH_BRAKE', label: EVENT_LABEL.HARSH_BRAKE },
    { value: 'HARSH_ACCEL', label: EVENT_LABEL.HARSH_ACCEL },
    { value: 'HARSH_CORNER', label: EVENT_LABEL.HARSH_CORNER },
    { value: 'OVERSPEED', label: EVENT_LABEL.OVERSPEED },
];

const SafetyView = (props) => {
    const {
        events, availability, speedTrips, trips, timezone, error, tooLarge, onFocusEvent,
    } = props;

    const zone = timezone || 'UTC';
    const [typeFilter, setTypeFilter] = useState('');
    const rows = useMemo(() => (Array.isArray(events) ? events : []), [events]);
    // The filter is a lens on the table, not on the window: the tiles above keep counting every
    // event, which is what makes "3 of 18" in the badge mean something.
    const visible = useMemo(
        () => (typeFilter ? rows.filter((row) => row.type === typeFilter) : rows),
        [rows, typeFilter],
    );
    const tripNumbers = useMemo(() => buildTripNumbers(trips), [trips]);
    const showAsset = useMemo(() => new Set(rows.map((row) => row.assetName)).size > 1, [rows]);

    const columns = useMemo(
        () => safetyColumns({ showAsset, tripNumbers, zone }),
        [showAsset, tripNumbers, zone],
    );

    // Safety has no server rollup by design — six one-liners over the rows, the way
    // OverviewView computes its tiles. Types are matched on a substring so a label change
    // ('harshBraking' vs 'Harsh braking') does not silently blank a tile.
    //
    // Peak speed is the exception: it comes from `speedTrips` (the server's peakSpeedByTrip, a max
    // over every sample in each trip), NOT from `events[].peakKmh`. An event's peakKmh is the speed
    // *at that event*, so a fleet with no overspeed markers — the common case, the signal is
    // unpopulated on most devices — made this tile report the speed at the harshest brake instead of
    // the window's top speed. Measured on one asset-month: the tile read 119 km/h where the
    // telemetry's true max was 180. Same trap telemetrySeries.js documents for the daily peak.
    const tiles = useMemo(() => {
        const countOf = (needle) => rows
            .filter((row) => String(row.type ?? '').toLowerCase().includes(needle)).length;
        const peaks = (Array.isArray(speedTrips) ? speedTrips : [])
            .map((trip) => trip.maxKmh).filter(Number.isFinite);

        return [
            { key: 'total', label: 'Total events', value: rows.length },
            { key: 'brake', label: 'Harsh braking', value: countOf('brak') },
            { key: 'accel', label: 'Harsh acceleration', value: countOf('accel') },
            { key: 'corner', label: 'Harsh cornering', value: countOf('corner') },
            { key: 'overspeed', label: 'Overspeed episodes', value: countOf('overspeed') },
            {
                key: 'peak',
                label: 'Peak speed',
                value: peaks.length > 0 ? Math.round(Math.max(...peaks)) : '—',
                unit: peaks.length > 0 ? 'km/h' : undefined,
            },
        ];
    }, [rows, speedTrips]);

    const emptyState = () => {
        if (error) {
            return {
                title: tooLarge ? 'Window too large' : 'Unable to load safety events',
                text: error,
            };
        }
        // Only an explicit "available" earns the clean-driving reading. Anything else —
        // absent, or never answered — has to say the signal is missing instead.
        if (availability === 'available') {
            return {
                title: 'No safety events in this window',
                text: 'Telemetry covered this window and recorded no harsh braking, acceleration,'
                    + ' cornering or overspeed episodes. That is clean driving, not missing data.',
            };
        }
        return {
            title: 'No driving-event signal in this window',
            text: 'No greenDriving or overspeeding telemetry came back for the selected period, so'
                + ' there is nothing to detect events from. A zero here would mean the signal is'
                + ' missing — not that the driving was clean.',
        };
    };

    const empty = rows.length === 0 ? emptyState() : null;
    const filtered = visible.length !== rows.length;

    return (
        <div className="od-view">
            <OdKpiRow tiles={tiles} />

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">
                            Safety events
                            <span className="od-badge od-badge-neutral">
                                {filtered ? `${visible.length} of ${rows.length}` : `${rows.length}`}
                            </span>
                        </div>
                        <div className="od-card-subtitle">
                            Harsh braking, acceleration, cornering and overspeed episodes per trip
                        </div>
                    </div>
                    <div className="od-card-actions">
                        <OdSelect
                          label="Type"
                          value={typeFilter}
                          onChange={setTypeFilter}
                          options={TYPE_OPTIONS}
                          ariaLabel="Filter by event type"
                        />
                    </div>
                </div>

                {empty ? (
                    <div className="od-empty">
                        <div className="od-empty-title">{empty.title}</div>
                        <div className="od-empty-text">{empty.text}</div>
                    </div>
                ) : (
                    <div className="od-card-body od-table-wrap">
                        <Table
                          tableData={visible}
                          columns={columns}
                          module="odAnalytics"
                          // The row is the obvious target; the ◉ button is the precise one, not
                          // the only one. Both land on the same handler.
                          onRowClicked={onFocusEvent}
                          highlightOnHover
                          pointerOnHover
                          pagination={visible.length > 10}
                          paginationServer={false}
                          paginationPerPage={10}
                          handlePageChange={() => {}}
                          handlePerRowsChange={() => {}}
                          setSelectedRowCount={() => {}}
                          totalRows={visible.length}
                          customStyles={odTableStyles}
                          backgroundColor="#F9FAFB"
                        />
                    </div>
                )}

                <div className="od-card-note">
                    An event is one detected edge, not one telemetry payload: a harsh-braking flag held
                    across several packets counts once. Force and speed are blank when the device did
                    not report them — the event still happened.
                </div>
            </div>
        </div>
    );
};

export default SafetyView;
